import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { useState, useMemo, useRef } from "react";
import Spinner from "@/react/components/Spinner";
import Tag from "@/react/components/Tag";
import { addAbsoluteReactComponent } from "@/bootstrap";
import Tippy from '@tippyjs/react';

async function fetchTags({ queryKey }) {
	const [, { url, search }] = queryKey;
	const fullUrl = new URL(url);
	if (search) {
		fullUrl.searchParams.set("search", search);
	}
	const res = await ApiClient.get(fullUrl);
	return res.data.data;
}

export default function TagListWrapper(props) {
	const client = useMemo(() => new QueryClient(), []);
	return (
		<QueryClientProvider client={client}>
			<TagList {...props} />
		</QueryClientProvider>
	);
}

function TagList({ qkey = "ro-tags", url = `${window.API_URL}ro-tags?limit=10`, roId }) {
	const [search, setSearch] = useState("");
	const inputRef = useRef()
	const { data: tags = [], isLoading, isError } = useQuery({
		queryKey: [qkey, { url, search }],
		queryFn: fetchTags,
		keepPreviousData: true,
	});

	const handleSearch = () => {
		const value = inputRef.current.value;
		setSearch(value);
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			e.stopPropagation();
			handleSearch()
		}
	};

	const handleClick = (props) => {
		// Dispatch a custom event that bubbles up to the document
		const event = new CustomEvent("ro:tagClick", {
			bubbles: true,
			detail: { tag: props },
		});
		document.dispatchEvent(event);

	};

	return (
		<div className="flex flex-col ctx-search-menu w-[210px]">
			{/* Search Bar */}
			<div className="ctx-search-input-field w-full bg-white">
				<input
					ref={inputRef}
					className="ctx-search-input"
					type="text"
					placeholder="Search..."
					onKeyDown={handleKeyDown}
				/>
				<button
					className="ctx-search-btn"
					onClick={handleSearch}
				>
					<span>search</span>
				</button>
			</div>

			{/* Tag Results */}
			<div className="flex items-center justify-center w-full">
				{isLoading ? (
					<Spinner size={30} showMessage={false} />
				) : isError ? (
					<div className="text-secondary">Failed to load tags.</div>
				) : (
					<div className="grid grid-cols-2 gap-2 bg-white">
						<span className="col-span-2">Select tag</span>
						{
							tags.map((tag) => <RoTag key={tag.id} {...tag} onClick={handleClick} />)
						}
					</div>
				)}
			</div>
		</div>
	);
}

const RoTag = ({ onClick, ...props }) => {
	return (
		<div className="animation-pop cursor-pointer" onClick={() => onClick(props)}>
			<Tag {...props} />
		</div>
	);
}

const tippyOptions = {
	placement: "bottom",
	theme: 'retention-schedule',
	animation: "shift-away",
	allowHTML: "true",
	interactive: "true",
	appendTo: () => document.body,
}


const RoTagInTable = (props) => {
	const meta = typeof props.meta === 'string' ? JSON.parse(props.meta) : props.meta;
	const { title, description } = props;
	const tippyRef = useRef(null);

	const deleteHandler = () => {
		// Dispatch a custom event that bubbles up to the document
		const event = new CustomEvent("ro:tagDeleteClick", {
			bubbles: true,
			detail: { tag: props },
		});
		
		tippyRef.current?._tippy.hide();
		document.dispatchEvent(event);
	};

	const tooltip = (
		<div className="flex flex-col gap-2 min-w-[80px]">
			<div className="flex justify-between items-center">
				<h3 className="font-medium">{title}</h3>
				<button
					className="hover:text-secondary bg-transparent text-lg ml-2"
					onClick={deleteHandler}
				>
					<span className="fk-icons">delete</span>
				</button>
			</div>
			<hr />
			<div>{description ?? <em>This tag has no description</em>}</div>
		</div>
	);

	return (
		<Tippy content={tooltip} {...tippyOptions} ref={tippyRef}>
			<Tag {...props} meta={meta} />
		</Tippy>
	);
};


addAbsoluteReactComponent({ RoTag: RoTagInTable })